import React, { useEffect, useState } from 'react';

//hooks
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

//actions
import {
  fetchOrderOverviewDetails,
  fetchOrderCampaignsDetails,
  fetchOrderBatchingDetails,
  payAndProcess,
  cancelOrder,
} from '~/redux/actions/orders-actions';

// components
import CustomStepper from './Overview/Stepper';
import OrderContacts from '~/components/Orders/OrderDetailsNew/Contacts';
import TotalCost from '~/components/Orders/OrderDetailsNew/Overview/TotalCost';
import HoldStepper from '~/components/Orders/OrderDetailsNew/Overview/HoldStepper';
import DeliveryInfo from '~/components/Orders/OrderDetailsNew/Overview/DeliveryInfo';
import Analytics from '~/components/Orders/OrderDetailsNew/Overview/Analytics';
import CampaignDetails from '~/components/Orders/OrderDetailsNew/CampaignDetails';
import BatchingDetails from '~/components/Orders/OrderDetailsNew/BatchingDetails';
import RescheduleOrderModal from '~/components/AdminPortal/Orders/RescheduleOrderModal';
import OlcActionMenu from '~/components/General/OlcActionMenu';
import OlcPageBackButton from '~/components/General/OlcPageBackButton';
import FindNewContactsSummary from '~/components/Orders/OrderDetailsNew/Overview/FindNewContactsSummary';

// MUI Components
import {
  Box,
  Card,
  Tabs,
  Tab,
  MenuItem,
  CircularProgress,
  GlobalStyles,
} from '@mui/material';

// Components
import HeaderCard from '~/components/Orders/OrderDetailsNew/HeaderCard';
import ConfirmModal from '~/components/General/ConfirmModal';
import AddFunds from '~/components/Account/Wallet/AddFunds';
import OlcButton from '~/components/General/OlcButton';

// Styles
import './styles.scss';
import {
  CancelCIcon,
  MailerProofCIcon,
  PayAndProcessCIcon,
  ProcessOrderCIcon,
  RescheduleCIcon,
  CancelOrderDetailsCIcon,
  InsufficientCIcon,
} from '~/assets/images';
import { failure, success } from '~/redux/actions/snackbar-actions';
import { createEnvelopeProof, createViewProofForOrder } from '~/utils/template-builder';
import moment from 'moment';
import { MESSAGES } from '~/utils/message';
import { dateFormat, doesItFallInCancellationPeriod } from '~/utils/date-format';
import { getUserData } from '~/redux/actions/auth-actions';
import OrderStats from './Overview/OrderStats';
import { getFindNewContactsSearchMethod } from './utils/find-new-contacts';

const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`order-tabpanel-${index}`}
      aria-labelledby={`order-tab-${index}`}
      {...other}
    >
      {value === index && <Box className="tabPanelContent">{children}</Box>}
    </div>
  );
};

const isFindNewContactsOrder = (stepper, overview) => Boolean(
  stepper?.isFindNewContactsPurchaseOnly ||
    overview?.isFindNewContactsPurchaseOnly ||
    stepper?.meta?.findNewContactsPurchase ||
    stepper?.meta?.source === 'find_new_contacts_data_axle' ||
    overview?.contactImportLog?.meta?.source === 'find_new_contacts_data_axle' ||
    stepper?.contactImportLogs?.[0]?.meta?.source === 'find_new_contacts_data_axle'
);

const OrderDetailsNew = () => {
  const [tabValue, setTabValue] = useState(0);
  const [proofLoading, setProofLoading] = useState(false);
  const [envelopeProofLoading, setEnvelopeProofLoading] = useState(false);
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false);
  const [dialog, setDialog] = useState({
    open: false,
    message: '',
    loading: false,
    currentAction: null,
    params: null,
  });
  const [fundsModal, setFundsModal] = useState({ open: false, params: null });
  const actionsOpen = Boolean(actionsAnchorEl);
  const handleActionsClick = (event) => {
    setActionsAnchorEl(event.currentTarget);
  };
  const handleActionsClose = () => {
    setActionsAnchorEl(null);
  };
  const detail = useSelector((state) => state.ordersReducers.detailOverview);
  const campaignDetails = useSelector(
    (state) => state.ordersReducers.OrderDetailsCampaigns?.automation
  );
  const campaignOrderId = useSelector(
    (state) => state.ordersReducers.OrderDetailsCampaigns?.orderId
  );
  const orderBatchingUuid = useSelector(
    (state) => state.ordersReducers.detailOverview?.stepper?.orderBatchingUuid
  );
  const rosCost = useSelector(
    (state) => state.ordersReducers.detailOverview?.stepper?.meta?.rosCost
  );
  const additionalPageCost = useSelector(
    (state) => state.ordersReducers.detailOverview?.stepper?.meta?.additionalPageCost
  );
  const hasAdditionalPage = useSelector(
    (state) => state.ordersReducers.detailOverview?.stepper?.meta?.hasAdditionalPage
  );
  const orderCost = useSelector((state) => state.ordersReducers.detailOverview?.overview?.cost);
  const { id } = useParams();
  const isFindNewContactsPurchase = isFindNewContactsOrder(detail?.stepper, detail?.overview);
  const findNewContactsImportLog =
    detail?.overview?.contactImportLog || detail?.stepper?.contactImportLogs?.[0];
  const findNewContactsSearchMethod = getFindNewContactsSearchMethod(
    detail?.stepper,
    detail?.overview
  );

  const isLoading =
    detail?.loading || Boolean(detail?.stepper?.id && String(detail.stepper.id) !== String(id));

  const dispatch = useDispatch();

  const navigate = useNavigate();

  useEffect(() => {
    const controller = new AbortController();

    window.scrollTo({ top: 0, behavior: 'smooth' });

    const fetchOrderData = async () => {
      const result = await dispatch(fetchOrderOverviewDetails({ id, signal: controller.signal }));

      if (result?.aborted) return;

      if (result && result.status === 422 && result.shouldRedirect) {
        navigate('/orders');
        return;
      }

      const isFindNewContactsResult = isFindNewContactsOrder(
        result?.data?.stepper,
        result?.data?.overview
      );

      if ((!result || result.success !== false) && !isFindNewContactsResult) {
        dispatch(fetchOrderCampaignsDetails({ id }));
        dispatch(fetchOrderBatchingDetails({ id }));
      }
    };

    fetchOrderData();

    return () => controller.abort();
  }, [id, dispatch, navigate]);

  // useEffect(() => {
  //   const body = document.body;
  //   if (window.location.pathname.includes('/orders/detail')) {
  //     body.style.backgroundColor = '#F9FAFB';
  //   }
  //   return () => {
  //     body.style.backgroundColor = 'inherit';
  //   };
  // }, []);

  const tabs = [
    { label: 'Overview', id: 'overview' },
    { label: 'Contacts', id: 'contacts' },
    ...(!isFindNewContactsPurchase &&
    campaignDetails &&
    String(campaignOrderId) === String(id) &&
    !detail?.loading
      ? [{ label: 'Campaign Details', id: 'campaign-details' }]
      : []),
    ...(!isFindNewContactsPurchase &&
    orderBatchingUuid &&
    String(detail?.stepper?.id) === String(id) &&
    !detail?.loading
      ? [{ label: 'Batching Detail', id: 'batching-details' }]
      : []),
  ];

  // If the active tab no longer exists (Campaign/Batching tab was hidden), reset to Overview.
  // A single bounds check covers both tabs and any future conditional tabs.
  useEffect(() => {
    if (tabValue >= tabs.length) {
      setTabValue(0);
    }
  }, [tabs.length, tabValue]);

  const refreshOverview = async () => {
    const result = await dispatch(fetchOrderOverviewDetails({ id }));

    if (result && result.status === 422 && result.shouldRedirect) {
      navigate('/orders');
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const downloadViewProof = async () => {
    try {
      setProofLoading(true);
      await dispatch(
        createViewProofForOrder(
          `Order-${id}`,
          {
            orderId: id,
          },
          'order',
          detail.stepper?.templateType
        )
      );
      setProofLoading(false);
    } catch (error) {
      dispatch(failure(MESSAGES.VIEW_PROOF_ERROR));
      setProofLoading(false);
    }
  };

  const downloadEnvelopeProof = async () => {
    try {
      setEnvelopeProofLoading(true);
      let payload = {
        orderId: id,
      };
      await dispatch(createEnvelopeProof(`Order-${id}-Envelope-Proof`, payload));
    } catch (error) {
      dispatch(failure(MESSAGES.VIEW_PROOF_ERROR));
    } finally {
      setEnvelopeProofLoading(false);
    }
  };

  const handlePayAndProcessOrder = async () => {
    handleActionsClose();
    let currentDate = moment();
    setDialog({
      open: true,
      message: '',
      loading: false,
      currentAction: 'payAndProcess',
      params: { id, cost: orderCost },
      successButtonName: MESSAGES.ORDERS.DETAIL.MODALS.PAY_AND_PROCESS.SUCCESS_BUTTON,
      subMessage: `${MESSAGES.ORDERS.DETAIL.MODALS.PAY_AND_PROCESS.SUB_MESSAGE} ${dateFormat(currentDate, true)}`,
      heading: MESSAGES.ORDERS.DETAIL.MODALS.PAY_AND_PROCESS.HEADING_NEW,
      icon: ProcessOrderCIcon,
    });
  };

  const handleCancelOrder = async () => {
    handleActionsClose();
    setDialog({
      open: true,
      message: MESSAGES.ORDERS.DETAIL.MODALS.CANCEL.MESSAGE_NEW,
      subMessage: '',
      loading: false,
      currentAction: 'cancel',
      successButtonName: MESSAGES.ORDERS.DETAIL.MODALS.CANCEL.SUCCESS_BUTTON,
      heading: MESSAGES.ORDERS.DETAIL.MODALS.CANCEL.HEADING,
      icon: CancelOrderDetailsCIcon,
    });
  };

  const handleInsufficientFunds = (params) => {
    setDialog({
      open: true,
      message: MESSAGES.ORDERS.DETAIL.MODALS.ADD_FUNDS.MESSAGE,
      loading: false,
      currentAction: 'addFunds',
      params: params || { id, cost: orderCost },
      successButtonName: MESSAGES.ORDERS.DETAIL.MODALS.ADD_FUNDS.SUCCESS_BUTTON,
      subMessage: MESSAGES.ORDERS.DETAIL.MODALS.ADD_FUNDS.SUB_MESSAGE,
      heading: MESSAGES.ORDERS.DETAIL.MODALS.ADD_FUNDS.HEADING,
      icon: InsufficientCIcon,
    });
  };

  const handleCloseDialog = () => {
    setDialog({ open: false, message: '', loading: false, params: null, currentAction: null });
  };

  const handleOk = async () => {
    setDialog((prev) => ({ ...prev, loading: true }));
    if (dialog.currentAction === 'cancel') {
      const payload = { orderId: id };
      const response = await dispatch(cancelOrder(payload));
      if (response.status === 200) {
        dispatch(success(response.data.message));
        refreshOverview();
      } else {
        dispatch(failure(response.data.message));
      }
    } else if (dialog.currentAction === 'payAndProcess') {
      const payload = { orderId: id };
      const response = await dispatch(payAndProcess(payload));
      if (response.status === 200) {
        dispatch(getUserData());
        dispatch(success(response.data.message));
        refreshOverview();
      } else {
        if (
          response.data.message === 'Auto Funding Failed' ||
          response.data.message == 'Auto Funding is disabled'
        ) {
          setTimeout(() => {
            handleInsufficientFunds(dialog.params);
          }, 1000);
        } else {
          dispatch(failure(response.data.message));
        }
      }
    } else if (dialog.currentAction == 'addFunds') {
      setFundsModal({ open: true, params: dialog.params });
    }
    setDialog((prev) => ({ ...prev, loading: false, open: false }));
  };

  const handleRescheduleOrder = () => {
    setRescheduleModalOpen(true);
    handleActionsClose();
  };

  return (
    <Box className="orderDetailsNewContainer">
      <Box className="orderDetailsNewHeader">
        <OlcPageBackButton onClick={() => navigate('/orders')}>
          Back to Orders
        </OlcPageBackButton>
        {!isFindNewContactsPurchase && (
          <OlcButton
            variant="secondary"
            className="orderDetailsActionsButton"
            onClick={handleActionsClick}
            dropdown
            expanded={Boolean(actionsAnchorEl)}
            disabled={isLoading}
          >
            Actions
          </OlcButton>
        )}
      </Box>
      <HeaderCard
        loading={isLoading}
        orderNumber={detail?.stepper?.id}
        orderName={detail?.stepper?.name}
        templateId={
          isFindNewContactsPurchase
            ? findNewContactsImportLog?.id || detail?.stepper?.orderUuid
            : detail?.stepper?.templateId
        }
        templateName={
          isFindNewContactsPurchase
            ? detail?.stepper?.importListName ||
              findNewContactsImportLog?.meta?.listName ||
              findNewContactsImportLog?.fileName
            : detail?.stepper?.template?.title
        }
        productType={
          isFindNewContactsPurchase ? 'Find New Contacts' : detail?.stepper?.product?.productType
        }
        searchMethod={isFindNewContactsPurchase ? findNewContactsSearchMethod : undefined}
        rosOfferPercentage={detail?.stepper?.meta?.rosOfferPercentage}
        isFindNewContactsPurchase={isFindNewContactsPurchase}
      />

      <Card className="orderTabsCard">
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          className="orderTabs"
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
        >
          {tabs.map((tab, index) => (
            <Tab
              key={tab.id}
              label={tab.label}
              id={`order-tab-${index}`}
              aria-controls={`order-tabpanel-${index}`}
              className="orderTab"
              disableRipple
            />
          ))}
        </Tabs>

        {tabs.map((tab, index) => (
          <TabPanel key={tab.id} value={tabValue} index={index}>
            {tab.id === 'overview' && detail && (
              <Box>
                {!isFindNewContactsPurchase &&
                  detail?.stepper?.status !== 'On Hold' &&
                  detail?.stepper?.status !== 'Canceled' && (
                    <CustomStepper
                      info={detail.stepper}
                      cancellationWindow={detail.overview.cancellationWindow}
                      refreshOverview={refreshOverview}
                      loading={isLoading}
                    />
                  )}
                {!isFindNewContactsPurchase &&
                  (detail?.stepper?.status == 'On Hold' ||
                    detail?.stepper?.status == 'Canceled') && (
                  <HoldStepper
                    info={detail.stepper}
                    refreshOverview={refreshOverview}
                    loading={isLoading}
                    orderCost={orderCost}
                  />
                )}
                {isFindNewContactsPurchase ? (
                  <FindNewContactsSummary data={detail.stepper} overview={detail.overview} />
                ) : (
                  <>
                    <TotalCost
                      info={detail.overview}
                      loading={isLoading}
                      rosCost={rosCost}
                      orderCost={orderCost}
                      additionalPageCost={additionalPageCost}
                      hasAdditionalPage={hasAdditionalPage}
                    />
                    {/* Analytics (Tracking/Scanning charts) — temporarily hidden.
                    <Analytics
                      loading={isLoading}
                      info={detail.analytics}
                      stepper={detail.stepper}
                    /> */}
                  <DeliveryInfo
                    stepper={detail.stepper}
                    overview={detail.overview}
                    info={detail.analytics}
                    loading={isLoading}
                  />
                    <OrderStats data={detail.stepper} overview={detail.overview} loading={isLoading} />
                  </>
                )}
              </Box>
            )}
            {tab.id === 'contacts' && <OrderContacts refreshOverview={refreshOverview} />}
            {tab.id === 'campaign-details' && <CampaignDetails />}
            {tab.id === 'batching-details' && <BatchingDetails />}
          </TabPanel>
        ))}
      </Card>
      {!isLoading && !isFindNewContactsPurchase && (
        <OlcActionMenu
          id="basic-menu"
          anchorEl={actionsAnchorEl}
          open={actionsOpen}
          onClose={handleActionsClose}
          disableScrollLock
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          sx={{
            marginTop: '4px',
          }}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
        >
          {detail?.stepper?.paymentStatus !== 'Paid' && (
            <MenuItem onClick={handleRescheduleOrder}>
              <img src={RescheduleCIcon} alt="Reschedule" />
              Reschedule
            </MenuItem>
          )}
          {(detail?.stepper?.status === 'Canceled' || detail?.stepper?.status === 'On Hold') && (
            <MenuItem onClick={handlePayAndProcessOrder}>
              <img src={PayAndProcessCIcon} alt="Pay and Process" />
              Pay and Process
            </MenuItem>
          )}
          {detail?.stepper?.status == 'Scheduled' &&
            doesItFallInCancellationPeriod(detail?.stepper?.deliveryDate) && (
              <MenuItem onClick={handleCancelOrder}>
                <img src={CancelCIcon} alt="Cancel" />
                Cancel
              </MenuItem>
            )}
          <MenuItem onClick={downloadViewProof} disabled={proofLoading}>
            {proofLoading ? (
              <>
                <CircularProgress size={14} sx={{ color: '#ed5c2f' }} />
                Downloading...
              </>
            ) : (
              <>
                <img src={MailerProofCIcon} alt="Mailer Proof" />
                Mailer Proof
              </>
            )}
          </MenuItem>
          {detail?.stepper?.product?.hasEnvelope && (
            <>
              <MenuItem onClick={downloadEnvelopeProof} disabled={envelopeProofLoading}>
                {envelopeProofLoading ? (
                  <>
                    <CircularProgress size={14} sx={{ color: '#ed5c2f' }} />
                    Downloading...
                  </>
                ) : (
                  <>
                    <img src={MailerProofCIcon} alt="Envelope Proof" />
                    Envelope Proof
                  </>
                )}
              </MenuItem>
            </>
          )}
        </OlcActionMenu>
      )}

      {dialog.open && (
        <ConfirmModal
          openConfirmModal={{
            open: dialog.open,
            message: dialog.message,
            subMessage: dialog.subMessage,
            header: dialog.heading,
            icon: dialog.icon,
            loading: dialog.loading,
            successButtonName: dialog.successButtonName,
            cancelButtonName: dialog.currentAction === 'cancel' ? 'No' : 'Cancel',
            type: dialog.currentAction === 'payAndProcess' ? 'payAndProcess' : '',
            handleClose: handleCloseDialog,
            handleOk,
          }}
        />
      )}

      {fundsModal.open && (
        <AddFunds
          params={fundsModal.params}
          open={fundsModal.open}
          handleOpen={() => {
            setFundsModal((prev) => ({ ...prev, open: true }));
          }}
          handleClose={() => {
            setFundsModal((prev) => ({ ...prev, open: false }));
          }}
          onSuccessCallback={async () => {
            const response = await dispatch(payAndProcess({ orderId: fundsModal.params.id }));
            if (response.status === 200) {
              dispatch(getUserData());
              dispatch(success(response.data.message));
              refreshOverview();
            } else {
              dispatch(failure(response.data.message));
            }
          }}
        />
      )}

      {!isFindNewContactsPurchase && (
        <RescheduleOrderModal
          openModal={rescheduleModalOpen}
          handleCloseModal={() => setRescheduleModalOpen(false)}
          selectedOrder={id}
          getOrders={refreshOverview}
          activeRow={detail?.stepper}
        />
      )}
    </Box>
  );
};

export default OrderDetailsNew;
